/**
 * Formatea un número con separadores de miles y decimales
 * @param value - El número a formatear
 * @param decimals - Número de decimales (default: 2)
 * @param locale - Locale para el formato (default: 'en-US' para comas en miles)
 * @returns String formateado (ej: "1,000.00")
 */
export function formatNumber(value: number | string | null | undefined, decimals: number = 2, locale: string = 'en-US'): string {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return '0.' + '0'.repeat(decimals);
  }

  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if valid number
  if (isNaN(numValue)) {
    return '0.' + '0'.repeat(decimals);
  }

  // Format with Intl.NumberFormat
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
}

/**
 * Formatea un número como moneda con símbolo
 * @param value - El número a formatear
 * @param currency - Código de moneda (default: 'USD')
 * @param locale - Locale para el formato (default: 'en-US')
 * @returns String formateado con símbolo de moneda (ej: "$1,000.00")
 */
export function formatCurrency(value: number | string | null | undefined, currency: string = 'USD', locale: string = 'en-US'): string {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(0);
  }

  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if valid number
  if (isNaN(numValue)) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(0);
  }

  // Format with Intl.NumberFormat
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(numValue);
}

/**
 * Formatea un número con separadores de miles sin decimales
 * @param value - El número a formatear
 * @param locale - Locale para el formato (default: 'en-US')
 * @returns String formateado (ej: "1,000")
 */
export function formatInteger(value: number | string | null | undefined, locale: string = 'en-US'): string {
  return formatNumber(value, 0, locale);
}

/**
 * Elimina el formato de un número (quita comas y convierte a número)
 * @param value - El string formateado
 * @returns Número sin formato
 */
export function unformatNumber(value: string): number {
  if (!value) return 0;
  // Remove all commas and parse as float
  const cleaned = value.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Redondea un número a 2 decimales correctamente.
 *
 * Soluciona el bug de JavaScript donde los números con punto flotante
 * no se redondean correctamente. Por ejemplo:
 * - 39.055.toFixed(2) = "39.05" (incorrecto, debería ser "39.06")
 * - round2(39.055) = 39.06 (correcto)
 *
 * Esto es crítico para cálculos de facturas donde:
 * - Precio: $36.50
 * - ITBMS 7%: $2.555 → debe redondear a $2.56
 * - Total: $36.50 + $2.56 = $39.06 (no $39.05)
 *
 * @param num - Número a redondear
 * @returns Número redondeado a 2 decimales
 *
 * @example
 * round2(36.50 * 0.07 + 36.50) // 39.06 (correcto)
 * (36.50 * 0.07 + 36.50).toFixed(2) // "39.05" (incorrecto)
 */
export function round2(num: number): number {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }
  // Usar Math.round con Number.EPSILON para compensar errores de punto flotante
  // Number.EPSILON es el menor número tal que 1 + EPSILON !== 1
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Redondea un número a 6 decimales para preservar precisión en precios (DGI 1-9p2-6).
 * Usa la misma técnica que round2 pero con 6 decimales.
 */
export function roundPrice(num: number): number {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }
  return Math.round((num + Number.EPSILON) * 1000000) / 1000000;
}

/**
 * Formatea un precio preservando hasta 6 decimales significativos (formato DGI 1-9p2-6).
 * Mínimo 2 decimales, máximo 6. Elimina ceros finales innecesarios después del 2do decimal.
 *
 * @example
 * formatPriceDecimals(2.5)        // "2.50"
 * formatPriceDecimals(2.56)       // "2.56"
 * formatPriceDecimals(2.575269)   // "2.575269"
 * formatPriceDecimals(2.570000)   // "2.57"
 * formatPriceDecimals(2.5752690)  // "2.575269"
 */
export function formatPriceDecimals(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (isNaN(num)) return '0.00';

  // Formatear con 6 decimales máximo, luego quitar ceros finales pero mantener mínimo 2
  const full = num.toFixed(6);
  // Quitar ceros finales después del 2do decimal
  const [intPart, decPart] = full.split('.');
  let trimmed = decPart.replace(/0+$/, '');
  if (trimmed.length < 2) trimmed = trimmed.padEnd(2, '0');
  return `${intPart}.${trimmed}`;
}

/**
 * Formatea un precio para mostrar en la UI con separadores de miles y hasta 6 decimales (DGI 1-9p2-6).
 * Usa formatPriceDecimals internamente y agrega separadores de miles.
 *
 * @example
 * formatPriceDisplay(1234.575269)   // "1,234.575269"
 * formatPriceDisplay(2.50)          // "2.50"
 * formatPriceDisplay(1000000.12)    // "1,000,000.12"
 */
export function formatPriceDisplay(value: number | string | null | undefined): string {
  const raw = formatPriceDecimals(value);
  const [intPart, decPart] = raw.split('.');
  const formattedInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(parseInt(intPart));
  return `${formattedInt}.${decPart}`;
}
